import { FC } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { PageWrapper } from '@/components/ui';
import api from '@/utils/api';
import {
  ChartResponse, MarketingKPIs, MxmLead, SxmBooking, TabKey, DateRangeKey
} from '@/features/dashboard/types';
import {
  LEAD_STATUS_COLORS, BOOKING_STATUS_COLORS, COLORS
} from '@/features/dashboard/constants';
import {
  DashboardHeader,
  KPIGrid,
  DashboardChart,
  LeadPipeline,
  TopContentList,
  DashboardSidebar,
  LeadSourceAnalytics,
  SkeletonKPICard,
  SkeletonChart,
  SkeletonPipeline,
  SkeletonList,
  SkeletonPulse
} from '@/features/dashboard/components';

const ExternalDashboard: FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('sales');
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRangeKey>('7d');

  const [chartData, setChartData] = useState<ChartResponse | null>(null);
  const [marketingKPIs, setMarketingKPIs] = useState<MarketingKPIs | null>(null);
  const [leads, setLeads] = useState<MxmLead[]>([]);
  const [bookings, setBookings] = useState<SxmBooking[]>([]);

  // ===================== DATA FETCHING =====================
  const getDateFilter = useCallback(() => {
    const now = new Date();
    const days = dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
    const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: now.toISOString() };
  }, [dateRange]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    const { from, to } = getDateFilter();
    try {
      const [chartRes, kpisRes, leadsRes, bookingsRes] = await Promise.all([
        api.get<ChartResponse>('/dashboard/charts', { params: { tab: activeTab, from, to } }),
        api.get<MarketingKPIs>('/mxm/posts/dashboard/kpis', { params: { from, to } }).catch(() => ({ data: null })),
        api.get<{ data: MxmLead[]; total: number }>('/mxm/leads/all', { params: { limit: 50, from, to } }).catch(() => ({ data: { data: [], total: 0 } })),
        api.get<{ data: SxmBooking[]; total: number }>('/sxm/bookings', { params: { limit: 50, from, to } }).catch(() => ({ data: { data: [], total: 0 } })),
      ]);
      setChartData(chartRes.data);
      setMarketingKPIs(kpisRes.data);
      setLeads(leadsRes.data?.data || []);
      setBookings(bookingsRes.data?.data || []);
    } catch (e) {
      console.error('Dashboard fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [activeTab, getDateFilter]);

  useEffect(() => { fetchAllData(); }, [fetchAllData]);

  // ===================== COMPUTED DATA =====================
  const leadsByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    leads.forEach(l => { counts[l.status || 'new'] = (counts[l.status || 'new'] || 0) + 1; });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name: name.replace(/_/g, ' '),
        value,
        color: LEAD_STATUS_COLORS[name] || COLORS[0]
      }));
  }, [leads]);

  const bookingsByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    bookings.forEach(b => { counts[b.status || 'confirmed'] = (counts[b.status || 'confirmed'] || 0) + 1; });
    return Object.entries(counts)
      .filter(([_, v]) => v > 0)
      .map(([name, value]) => ({ name, value, color: BOOKING_STATUS_COLORS[name] || '#6366f1' }));
  }, [bookings]);

  const upcomingBookings = useMemo(() => {
    const now = new Date();
    return bookings
      .filter(b => new Date(`${b.date}T${b.time}`) > now && b.status === 'confirmed')
      .sort((a, b) => new Date(`${a.date}T${a.time}`).getTime() - new Date(`${b.date}T${b.time}`).getTime())
      .slice(0, 5);
  }, [bookings]);

  const recentLeads = useMemo(() =>
    [...leads].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5),
    [leads]);

  const conversionRate = useMemo(() => leads.length ? ((leads.filter(l => l.status === 'booked').length / leads.length) * 100) : 0, [leads]);
  const pendingFollowUp = useMemo(() => leads.filter(l => l.nextOutreachAt && new Date(l.nextOutreachAt) <= new Date() && !l.paused).length, [leads]);

  // ===================== RENDER =====================
  if (loading && !chartData) {
    return (
      <PageWrapper title="Dashboard" hideHeader containerClassName="p-0 bg-[#f8f9fb] min-h-screen">
        <div className="max-w-[1920px] mx-auto">
          {/* Skeleton Header */}
          <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-slate-100">
            <div className="px-6 lg:px-10 py-4 flex items-center justify-between">
              <div className="flex items-center gap-6">
                <SkeletonPulse className="h-6 w-28" />
                <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
                  {[1, 2, 3].map(i => <SkeletonPulse key={i} className="h-8 w-20 rounded-lg" />)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <SkeletonPulse className="h-8 w-32 rounded-lg" />
                <SkeletonPulse className="h-8 w-20 rounded-lg" />
              </div>
            </div>
          </header>

          <div className="p-6 lg:p-10">
            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 xl:col-span-9 space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map(i => <SkeletonKPICard key={i} />)}
                </div>
                <SkeletonChart height="h-72" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <SkeletonPipeline />
                </div>
              </div>
              <div className="col-span-12 xl:col-span-3 space-y-6">
                <SkeletonList />
                <SkeletonList />
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title="Dashboard" hideHeader containerClassName="p-0 bg-[#f8f9fb] min-h-screen">
      <div className="max-w-[1920px] mx-auto">

        <DashboardHeader
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          dateRange={dateRange}
          setDateRange={setDateRange}
          onRefresh={fetchAllData}
          loading={loading}
        />

        <div className="p-6 lg:p-10">
          <div className="grid grid-cols-12 gap-6">

            <main className="col-span-12 xl:col-span-9 space-y-6">
              <KPIGrid
                loading={loading}
                activeTab={activeTab}
                leads={leads}
                bookings={bookings}
                marketingKPIs={marketingKPIs}
                chartData={chartData}
                conversionRate={conversionRate}
                pendingFollowUp={pendingFollowUp}
              />

              <DashboardChart loading={loading} chartData={chartData} />

              {activeTab === 'sales' && !loading && (
                <LeadSourceAnalytics chartData={chartData} />
              )}

              {activeTab === 'marketing' && !loading && (
                <LeadSourceAnalytics chartData={chartData} />
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {loading ? (
                  <>
                    <SkeletonPipeline />
                    <SkeletonList />
                  </>
                ) : (
                  <>
                    {activeTab === 'sales' && (
                      <>
                        <LeadPipeline leads={leads} leadsByStatus={leadsByStatus} />
                      </>
                    )}
                    {activeTab === 'marketing' && (
                      <TopContentList marketingKPIs={marketingKPIs} />
                    )}
                  </>
                )}
              </div>
            </main>

            <DashboardSidebar
              loading={loading}
              activeTab={activeTab}
              leads={leads}
              bookings={bookings}
              marketingKPIs={marketingKPIs}
              upcomingBookings={upcomingBookings}
              bookingsByStatus={bookingsByStatus}
              recentLeads={recentLeads}
            />

          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default ExternalDashboard;
